import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';

import {
  Business,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
  Assignment,
  Add,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { analyticsAPI } from '../services/api';
import ClientActivityMatrix from '../components/ClientActivityMatrix';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isLoading: companiesLoading, isConsultantUser, clientCompanies, consultantCompanies } = useCompany();
  
  // Real data from API
  const [portfolioMetrics, setPortfolioMetrics] = useState<any>(null);
  const [urgentItems, setUrgentItems] = useState<any[]>([]);
  const [activityMatrix, setActivityMatrix] = useState<any>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [isLoadingMatrix, setIsLoadingMatrix] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matrixError, setMatrixError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      // Only load consultant dashboard data if user is a consultant
      if (!isConsultantUser) {
        setIsLoadingAnalytics(false);
        setIsLoadingMatrix(false);
        return;
      }
      
      try {
        setIsLoadingAnalytics(true);
        setIsLoadingMatrix(true);
        setError(null);
        setMatrixError(null);
        
        // Load real analytics data and activity matrix
        const [analyticsData, urgentItemsData, matrixData] = await Promise.all([
          analyticsAPI.getDashboardAnalytics(),
          analyticsAPI.getUrgentItems(),
          analyticsAPI.getClientActivityMatrix().catch((err) => {
            console.error('[ERROR] Failed to load activity matrix:', err);
            setMatrixError(err.response?.data?.error || 'Failed to load activity matrix');
            return null;
          })
        ]);
        
        setPortfolioMetrics(analyticsData);
        setUrgentItems(urgentItemsData.urgent_items || []);
        setActivityMatrix(matrixData);
        
        console.log('[OK] Dashboard analytics loaded:', analyticsData);
        console.log('[OK] Urgent items loaded:', urgentItemsData);
        console.log('[OK] Activity matrix loaded:', matrixData);
        
      } catch (error: any) {
        console.error('[ERROR] Error loading dashboard data:', error);
        setError(error.response?.data?.error || 'Failed to load dashboard data');
      } finally {
        setIsLoadingAnalytics(false);
        setIsLoadingMatrix(false);
      }
    };

    if (user && !companiesLoading) {
      loadDashboardData();
    }
  }, [user, isConsultantUser, companiesLoading]);

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'overdue_audit':
        return <Warning color="error" />;
      case 'overdue_task':
        return <Assignment color="warning" />;
      case 'in_progress':
        return <Schedule color="info" />;
      default:
        return <CheckCircle color="success" />;
    }
  };

  const getItemColor = (type: string) => {
    switch (type) {
      case 'overdue_audit':
        return 'error';
      case 'overdue_task':
        return 'warning';
      case 'in_progress':
        return 'info';
      default:
        return 'success';
    }
  };

  // Auto-redirect client users with exactly one company to that company's dashboard
  useEffect(() => {
    if (!companiesLoading && !isConsultantUser && clientCompanies.length === 1) {
      console.log('🔄 Auto-redirecting to single company dashboard:', clientCompanies[0].name);
      navigate(`/company/${clientCompanies[0].id}`, { replace: true });
    }
  }, [companiesLoading, isConsultantUser, clientCompanies, navigate]);

  if (companiesLoading || isLoadingAnalytics) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Container>
    );
  }

  // Client user view - show their companies (only if more than one)
  if (!isConsultantUser) {
    // If only one company, the useEffect above will redirect - show loading in meantime
    if (clientCompanies.length === 1) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Redirecting to your company...</Typography>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome, {user?.first_name || user?.email}!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Your company dashboard
          </Typography>
        </Box>

        {/* Client Companies */}
        {clientCompanies.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
            {clientCompanies.map((company) => (
              <Card key={company.id} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <Business />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{company.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {company.industry || 'No industry set'}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(`/company/${company.id}`)}
                  >
                    View Company
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            You are not currently assigned to any companies. Please contact your administrator.
          </Alert>
        )}
      </Container>
    );
  }

  // Consultant user view - show full analytics dashboard
  // Note: Activity Matrix has its own loading/error handling, so we can render the dashboard
  // even if analytics API fails
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {user?.first_name || user?.username}!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Your consultant dashboard overview
        </Typography>
      </Box>

      {/* Quick Actions - Horizontal at top */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<Business />}
          onClick={() => navigate('/companies')}
          size="large"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
        >
          View All Companies
        </Button>
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/admin/companies')}
          size="large"
          color="secondary"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
        >
          Add New Organisation
        </Button>
        
        <Button
          variant="contained"
          startIcon={<TrendingUp />}
          onClick={() => navigate('/companies')}
          size="large"
          color="info"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
        >
          Portfolio Analytics
        </Button>
      </Box>

      {/* Analytics API Error - Display prominently if portfolio data failed */}
      {error && (
        <Box sx={{ mb: 4 }}>
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              Failed to load dashboard analytics
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Client Activity Matrix - Full Width */}
      <Box sx={{ mb: 4 }}>
        <ClientActivityMatrix 
          data={activityMatrix}
          isLoading={isLoadingMatrix}
          error={matrixError}
        />
      </Box>

      {/* Urgent Items - moved below matrix */}
      <Box sx={{ mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Urgent Items Requiring Attention
            </Typography>
            
            {/* Display error if urgent items failed to load */}
            {error && !urgentItems.length ? (
              <Alert severity="warning">
                Unable to load urgent items. Please refresh the page or contact support if the issue persists.
              </Alert>
            ) : urgentItems.length > 0 ? (
              <List>
                {urgentItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      onClick={() => navigate(`/company/${item.company_id || item.companyId}`)}
                      sx={{
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon>
                        {getItemIcon(item.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">
                              {item.company}
                            </Typography>
                            <Chip
                              label={item.type === 'overdue_audit' ? 'OVERDUE' : 
                                    item.type === 'overdue_task' ? 'TASK DUE' : 'IN PROGRESS'}
                              color={getItemColor(item.type) as any}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={item.description}
                      />
                    </ListItem>
                    {index < urgentItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Alert severity="success">
                No urgent items - all clients are up to date!
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Dashboard; 